import { useMemo } from "react";
import useSWR from "swr";
import type { Address } from "viem";
import { parseUnits } from "viem";

import { USD_DECIMALS } from "config/factors";
import { useShowDebugValues } from "context/SyntheticsStateContext/hooks/settingsHooks";
import { getAccountPnlSummary } from "lib/apis/accounts";
import { EMPTY_ARRAY } from "lib/objects";

export type PnlSummaryPoint = {
  bucketLabel: string;
  losses: number;
  pnlBps: bigint;
  pnlUsd: bigint;
  realizedPnlUsd: bigint;
  unrealizedPnlUsd: bigint;
  startUnrealizedPnlUsd: bigint;
  volume: bigint;
  wins: number;
  winsLossesRatioBps: bigint | undefined;
  usedCapitalUsd: bigint;
} & PnlSummaryPointDebugFields;

export type PnlSummaryPointDebugFields = {
  // #region Debug fields
  // Present only when showDebugValues is true
  realizedBasePnlUsd: bigint;
  realizedFeesUsd: bigint;
  realizedPriceImpactUsd: bigint;
  unrealizedBasePnlUsd: bigint;
  unrealizedFeesUsd: bigint;
  startUnrealizedBasePnlUsd: bigint;
  startUnrealizedFeesUsd: bigint;
  // #endregion
};

type PnlSummaryData = PnlSummaryPoint[];

/**
 * Parse a USD value string from the backend into a 30-decimal bigint.
 *
 * The backend currently returns plain decimal dollar strings like "0.9992" or "-123.45"
 * (after the decimal normalization fix). We use viem's parseUnits which handles
 * decimal strings correctly and converts to 30-decimal precision (USD_DECIMALS = 30).
 *
 * Falls back to 0n for empty, null, or unparseable values.
 */
function parseUsdString(v: string | undefined | null): bigint {
  if (!v || v === "0") return 0n;
  const normalized = String(v).trim();
  if (!normalized || normalized === "0") return 0n;

  if (/^-?\d+$/.test(normalized) && normalized.length > 20) {
    try {
      return BigInt(normalized);
    } catch (_error) {
      return 0n;
    }
  }

  try {
    // parseUnits("0.9992", 30) correctly produces the 30-dec bigint
    return parseUnits(normalized as `${number}`, USD_DECIMALS);
  } catch (_error) {
    try {
      return BigInt(normalized);
    } catch (_error) {
      return 0n;
    }
  }
}

/**
 * Parse a volume string — volume may be stored as a raw 30-dec integer string
 * OR as a plain decimal. Handle both.
 */
function parseVolumeString(v: string | undefined | null): bigint {
  if (!v || v === "0") return 0n;
  try {
    // Try plain BigInt first (raw 30-dec integer strings)
    return BigInt(v);
  } catch (_error) {
    // Fall back to parseUnits for decimal strings
    try {
      return parseUnits(v as `${number}`, USD_DECIMALS);
    } catch (_error) {
      return 0n;
    }
  }
}

export function usePnlSummaryData(chainId: number, account: Address) {
  const showDebugValues = useShowDebugValues();
  const normalizedAccount = account?.toLowerCase();
  const { data, error } = useSWR(
    normalizedAccount ? ["accountPnlSummary", chainId, normalizedAccount, showDebugValues] : null,
    () => getAccountPnlSummary(normalizedAccount!, { chainId, debug: showDebugValues })
  );

  const transformedData: PnlSummaryData = useMemo(() => {
    if (!data?.points) {
      return EMPTY_ARRAY;
    }

    return data.points.map((row: any): PnlSummaryPoint => {
      const base = {
        bucketLabel: row.bucketLabel,
        losses: row.losses ?? 0,
        // pnlBps is a ratio string (basis points) — may not be present from our backend
        pnlBps: row.pnlBps ? BigInt(row.pnlBps) : 0n,
        pnlUsd: parseUsdString(row.pnlUsd),
        realizedPnlUsd: parseUsdString(row.realizedPnlUsd),
        unrealizedPnlUsd: parseUsdString(row.unrealizedPnlUsd),
        startUnrealizedPnlUsd: parseUsdString(row.startUnrealizedPnlUsd),
        volume: parseVolumeString(row.volume),
        wins: row.wins ?? 0,
        winsLossesRatioBps: row.winsLossesRatioBps ? BigInt(row.winsLossesRatioBps) : undefined,
        usedCapitalUsd: parseUsdString(row.usedCapitalUsd),
      };

      if (showDebugValues) {
        return {
          ...base,
          realizedBasePnlUsd: parseUsdString(row.realizedBasePnlUsd),
          realizedFeesUsd: parseUsdString(row.realizedFeesUsd),
          realizedPriceImpactUsd: parseUsdString(row.realizedPriceImpactUsd),
          unrealizedBasePnlUsd: parseUsdString(row.unrealizedBasePnlUsd),
          unrealizedFeesUsd: parseUsdString(row.unrealizedFeesUsd),
          startUnrealizedBasePnlUsd: parseUsdString(row.startUnrealizedBasePnlUsd),
          startUnrealizedFeesUsd: parseUsdString(row.startUnrealizedFeesUsd),
        };
      }

      return {
        ...base,
        realizedBasePnlUsd: 0n,
        realizedFeesUsd: 0n,
        realizedPriceImpactUsd: 0n,
        unrealizedBasePnlUsd: 0n,
        unrealizedFeesUsd: 0n,
        startUnrealizedBasePnlUsd: 0n,
        startUnrealizedFeesUsd: 0n,
      };
    });
  }, [data?.points, showDebugValues]);

  return useMemo(() => ({ data: transformedData, error, loading: !data && !error }), [data, error, transformedData]);
}
